import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';

import RadioGroup from '@shared/fields/RadioGroup';
import Field from '../fields/Field';
import Info from '../fields/Info';
import Btn from '../buttons/Btn';
import Link from '../link/Link';
import {PasswordField} from '../fields/PasswordField';

const Genders = [
    {
        text: 'Mme',
        value: 'Mme',
    },
    {
        text: 'Mr',
        value: 'M',
    },
];

const STUDY_TYPES = [
    {
        text: 'traditionnelle',
        value: 'traditional',
    },
    {
        text: 'en ligne',
        value: 'online',
    },
];

const getValidationSchema = (formForRecoveryPoints, formForProBooking) => {
    const commonSchema = {
        ...(formForProBooking && {
            email: Yup.string()
                .email('Veuillez saisir une adresse email valide.')
                .required('Ce champ est obligatoire.'),
        }),
        prenom: Yup.string()
            .matches(
                /^[a-zA-Z\u00C0-\u00FF\-\ ]+$/,
                'Ce champ ne peut contenir que des lettres, des espaces ou un tiret (-).'
            )
            .required('Ce champ est obligatoire.'),
        nom: Yup.string()
            .matches(
                /^[A-Za-z\u00C0-\u00FF\-\ ]+$/,
                'Ce champ ne peut contenir que des lettres, des espaces ou un tiret (-).'
            )
            .required('Ce champ est obligatoire.'),
        dateNaissance: Yup.date().required('Ce champ est obligatoire.'),
        phone: Yup.string()
            .matches(
                /^(?:(?:)0)\s*[1-9](?:[\s.-]*\d{2}){4}$/,
                'Le téléphone doit être au format 01 23 45 67 89'
            )
            .required('Ce champ est obligatoire.'),
        codepostal: Yup.string()
            .matches(/^\d+$/, 'Ce champ ne peut contenir que des chiffres.')
            .min(5, 'Ce champ doit comporter 5 chiffres.')
            .required('Ce champ est obligatoire.'),
        localite: Yup.string()
            .matches(
                /^[A-Za-z\u00C0-\u00FF\-\ ]+$/,
                'Ce champ ne peut contenir que des lettres, des espaces ou un tiret (-).'
            )
            .required('Ce champ est obligatoire.'),
    };

    if (formForRecoveryPoints) {
        return Yup.object().shape({
            ...commonSchema,
            lieu_naissance: Yup.string(),
            nom_jeune_fille: Yup.string(),
            neph: Yup.mixed()
                .nullable()
                .transform((value) => (value === '' ? null : value)),
            numvoie: Yup.mixed()
                .nullable()
                .transform((value) => (value === '' ? null : value)),
            nomvoie: Yup.mixed()
                .nullable()
                .transform((value) => (value === '' ? null : value)),
            email: Yup.string()
                .trim("Ce champ ne peut pas inclure d'espace au début et à la fin")
                .strict(true)
                .email('Merci de renseigner une adresse email valide.')
                .required('Ce champ est obligatoire.'),
            password: Yup.string()
                .required('Ce champ est obligatoire.')
                .min(6, 'Votre mot de passe doit comporter au minimum 6 caractères.')
                .max(128, 'Votre mot de passe ne doit pas excéder 128 caractères.'),

        });
    } else if (formForProBooking) {
        return Yup.object().shape({
            ...commonSchema,
            lieu_naissance: Yup.string(),
            nom_jeune_fille: Yup.string(),
            neph: Yup.mixed()
                .nullable()
                .transform((value) => (value === '' ? null : value)),
            numvoie: Yup.mixed()
                .nullable()
                .transform((value) => (value === '' ? null : value)),
            nomvoie: Yup.mixed()
                .nullable()
                .transform((value) => (value === '' ? null : value)),
        });
    } else {
        return Yup.object().shape({
            ...commonSchema,
            neph: Yup.string()
                .matches(/^\d+$/, 'Votre NEPH ne doit contenir que des chiffres.')
                .min(12, 'Votre NEPH doit comporter 12 chiffres.')
                .max(12, 'Votre NEPH doit comporter 12 chiffres.')
                .required('Ce champ est obligatoire.'),
            nomvoie: Yup.string()
                .matches(
                    /^[A-Za-z0-9\u00C0-\u00FF\-\ \.\,\;\/]+$/,
                    'Ce champ ne peut contenir que des lettres, des espaces ou un tiret (-)'
                )
                .required('Ce champ est obligatoire.'),
            numvoie: Yup.string(),
            adresse: Yup.string(),
            email: Yup.string()
                .trim("Ce champ ne peut pas inclure d'espace au début et à la fin")
                .strict(true)
                .email('Merci de renseigner une adresse email valide.')
                .required('Ce champ est obligatoire.'),
            password: Yup.string()
                .required('Ce champ est obligatoire.')
                .min(6, 'Votre mot de passe doit comporter au minimum 6 caractères.')
                .max(128, 'Votre mot de passe ne doit pas excéder 128 caractères.'),

        });
    }
};

const StyledForm = styled(Form)`
    max-width: 386px;
`;

const Title = styled.div`
    font-size: 18px;
    font-weight: bold;
    line-height: 1.33;
    letter-spacing: 0.2px;
    color: #354354;
    margin-bottom: 16px;
    ${(props) =>
            props.marginTop &&
            `
    margin-top: 40px;
  `}
`;

class ExamRegForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedGender: Genders[1].value,
            selectedStudyType: STUDY_TYPES[0].value,
        };
    }

    onGenderChange = (selectedGender) => {
        this.setState({
            selectedGender,
        });
    };

    onStudyTypeChange = (type) => {
        this.setState({
            selectedStudyType: type,
        });
    };

    render() {
        const {examCenterType, onFormSubmit, onClickGoToSignInForm, formForRecoveryPoints, formForProBooking} =
            this.props;
        const {selectedGender, selectedStudyType} = this.state;
        const smallInputsWidth = '200px';
        const formForRecoveryPointsOrProBooking = formForRecoveryPoints || formForProBooking;

        return (
            <Formik
                initialValues={{
                    prenom: '',
                    nom: '',
                    dateNaissance: '',
                    phone: '',
                    neph: null,
                    numvoie: '',
                    nomvoie: null,
                    codepostal: '',
                    localite: '',
                    email: '',
                    password: '',
                    adresse: '',
                    lieu_naissance: '',
                    nom_jeune_fille: '',
                }}
                validationSchema={getValidationSchema(formForRecoveryPoints, formForProBooking)}
                onSubmit={(values, formikBag) => {
                    const updatedValues = {
                        ...values,
                        civilite: selectedGender,
                        study_type: selectedStudyType,
                    };

                    const filteredValues = Object.entries(updatedValues).reduce((acc, [key, value]) => {
                        if (value !== null && value !== undefined && value !== '') {
                            acc[key] = value;
                        }
                        return acc;
                    }, {});

                    return onFormSubmit(filteredValues, formikBag);
                }}
            >
                {({values, errors, touched, handleChange, handleBlur, isSubmitting}) => (
                    <StyledForm>
                        <Title>{formForProBooking ? "Informations sur le candidat" : "Vos informations personnelles"}</Title>
                        {formForProBooking && (
                            <Field
                                type="text"
                                label="Email"
                                placeholder="exemple@exemple.com"
                                name="email"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.email}
                                valid={touched.email && !errors.email}
                                errorMessage={errors.email && touched.email ? errors.email : null}
                            />
                        )}
                        <Field
                            type="text"
                            label="Prénom"
                            placeholder="Martin"
                            name="prenom"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.prenom}
                            valid={touched.prenom && !errors.prenom}
                            errorMessage={errors.prenom && touched.prenom ? errors.prenom : null}
                        />
                        <Field
                            type="text"
                            label="Nom"
                            placeholder="Dupont"
                            name="nom"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.nom}
                            valid={touched.nom && !errors.nom}
                            errorMessage={errors.nom && touched.nom ? errors.nom : null}
                        />
                        {formForRecoveryPointsOrProBooking && (
                            <Field
                                type="text"
                                label="Nom de jeune fille"
                                placeholder=""
                                name="nom_jeune_fille"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.nom_jeune_fille}
                                valid={touched.nom_jeune_fille && !errors.nom_jeune_fille}
                                errorMessage={
                                    errors.nom_jeune_fille && touched.nom_jeune_fille ? errors.nom_jeune_fille : null
                                }
                            />
                        )}
                        <Field
                            type="date"
                            label="Date de naissance"
                            name="dateNaissance"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.dateNaissance}
                            valid={touched.dateNaissance && !errors.dateNaissance}
                            errorMessage={
                                errors.dateNaissance && touched.dateNaissance ? errors.dateNaissance : null
                            }
                        />
                        {formForRecoveryPointsOrProBooking && (
                            <Field
                                type="text"
                                label="Lieu de naissance"
                                placeholder=""
                                name="lieu_naissance"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.lieu_naissance}
                                valid={touched.lieu_naissance && !errors.lieu_naissance}
                                errorMessage={
                                    errors.lieu_naissance && touched.lieu_naissance ? errors.lieu_naissance : null
                                }
                            />
                        )}
                        <Field
                            type="text"
                            label="Numéro de téléphone"
                            placeholder="01 23 45 67 89"
                            width={smallInputsWidth}
                            name="phone"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.phone}
                            valid={touched.phone && !errors.phone}
                            errorMessage={errors.phone && touched.phone ? errors.phone : null}
                        />
                        {!formForRecoveryPointsOrProBooking && (
                            <Field
                                type="text"
                                label="NEPH"
                                placeholder="09123813042"
                                width={smallInputsWidth}
                                name="neph"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.neph}
                                valid={touched.neph && !errors.neph}
                                errorMessage={errors.neph && touched.neph ? errors.neph : null}
                            />
                        )}
                        <RadioGroup
                            label="Civilité"
                            options={Genders}
                            currentOption={selectedGender}
                            onOptionChange={this.onGenderChange}
                        />
                        {!formForRecoveryPointsOrProBooking && (
                            <Field
                                type="text"
                                label="Numéro de la voie"
                                placeholder="77"
                                width={smallInputsWidth}
                                name="numvoie"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.numvoie}
                                valid={touched.numvoie && !errors.numvoie}
                                errorMessage={errors.numvoie && touched.numvoie ? errors.numvoie : null}
                            />
                        )}
                        {!formForRecoveryPointsOrProBooking && (
                            <Field
                                type="text"
                                label="Nom de la voie"
                                placeholder="Rue des Roses"
                                name="nomvoie"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.nomvoie}
                                valid={touched.nomvoie && !errors.nomvoie}
                                errorMessage={errors.nomvoie && touched.nomvoie ? errors.nomvoie : null}
                            />
                        )}
                        <Field
                            type="text"
                            label="Code postal"
                            placeholder="75018"
                            width={smallInputsWidth}
                            name="codepostal"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.codepostal}
                            valid={touched.codepostal && !errors.codepostal}
                            errorMessage={errors.codepostal && touched.codepostal ? errors.codepostal : null}
                        />
                        <Field
                            type="text"
                            label="Ville"
                            placeholder="Paris"
                            name="localite"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.localite}
                            valid={touched.localite && !errors.localite}
                            errorMessage={errors.localite && touched.localite ? errors.localite : null}
                        />
                        {!formForRecoveryPointsOrProBooking && (
                            <RadioGroup
                                label="Vous êtes inscrit(e) dans une auto-école"
                                options={STUDY_TYPES}
                                currentOption={selectedStudyType}
                                onOptionChange={this.onStudyTypeChange}
                            />
                        )}
                        {!formForProBooking && (
                            <>
                                <Title marginTop>Vos identifiants</Title>
                                <Field
                                    type="text"
                                    label="Email"
                                    placeholder="exemple@exemple.com"
                                    name="email"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.email}
                                    valid={touched.email && !errors.email}
                                    errorMessage={errors.email && touched.email ? errors.email : null}
                                />
                                <PasswordField
                                    placeholder="Minimum 6 caractères"
                                    name="password"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.password}
                                    valid={touched.password && !errors.password}
                                    errorMessage={errors.password && touched.password ? errors.password : null}
                                />
                            </>
                        )}
                        <Info examCenterType={formForRecoveryPointsOrProBooking ? "Lyra" : examCenterType}/>
                        <Btn text="Je m’inscris" block isLoading={isSubmitting} disabled={isSubmitting}/>
                        {!formForProBooking && (
                            <Link text="J’ai déjà un compte" block onClick={onClickGoToSignInForm}/>
                        )}
                    </StyledForm>
                )}
            </Formik>
        );
    }
}

ExamRegForm.propTypes = {
    examCenterType: PropTypes.string,
    onClickGoToSignInForm: PropTypes.func,
    onFormSubmit: PropTypes.func,
    formForRecoveryPoints: PropTypes.bool,
    formForProBooking: PropTypes.bool,
};

ExamRegForm.defaultProps = {
    examCenterType: '',
    onClickGoToSignInForm: () => {
    },
    onFormSubmit: () => {
    },
    formForRecoveryPoints: false,
    formForProBooking: false
};

export default ExamRegForm;
